import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import signaturesService, { type Signature, type CreateSignatureData } from '@/services/signatures.service';
import signatureTemplatesService, { type SignatureTemplate } from '@/services/signatureTemplates.service';
import { useOrganizations } from '@/hooks/useOrganizations';
import useAuthStore from '@/stores/authStore';
import usersService from '@/services/users.service';
import {
  Plus,
  Save,
  Check,
  AlertTriangle,
  Loader2,
  FileSignature,
  Users,
  Edit,
  Trash2,
  UserPlus,
  Search,
  Crown,
} from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export const SignatureSection: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  const isAdmin = currentUser?.role === 'admin';

  const { data: orgsData } = useOrganizations();
  const organizations = orgsData?.data ?? [];

  // All signatures (admin sees all, agent sees own)
  const { data: signaturesData, isLoading: isLoadingSignatures, error } = useQuery({
    queryKey: ['signatures'],
    queryFn: () => signaturesService.getUserSignatures(),
  });
  const signatures = signaturesData?.data ?? [];

  // Templates (admin only)
  const { data: templatesData, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['signature-templates'],
    queryFn: () => signatureTemplatesService.getTemplates(),
    enabled: isAdmin,
  });
  const templates = templatesData?.data ?? [];

  // Agents for assignment (admin only)
  const { data: agentsData } = useQuery({
    queryKey: ['agents'],
    queryFn: () => usersService.getAgents(false, 'active'),
    enabled: isAdmin,
  });
  const agents = agentsData?.data?.agents ?? [];

  // ==================== TEMPLATE STATE ====================
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SignatureTemplate | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateHtml, setTemplateHtml] = useState('');
  const [templateText, setTemplateText] = useState('');
  const [templateOrgId, setTemplateOrgId] = useState('');
  const [templateIsDefault, setTemplateIsDefault] = useState(false);
  const [templatePreviewMode, setTemplatePreviewMode] = useState<'html' | 'text'>('html');

  // Assignment dialog
  const [assigningTemplate, setAssigningTemplate] = useState<SignatureTemplate | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [assignmentSearch, setAssignmentSearch] = useState('');

  // Signature search/filter
  const [sigSearch, setSigSearch] = useState('');

  // Fetch assignments when assigning
  const { data: assignmentsData } = useQuery({
    queryKey: ['template-assignments', assigningTemplate?.id],
    queryFn: () => signatureTemplatesService.getAssignments(assigningTemplate!.id),
    enabled: !!assigningTemplate,
  });

  useEffect(() => {
    if (assignmentsData?.data) {
      setSelectedUserIds(assignmentsData.data.map(a => a.user_id));
    }
  }, [assignmentsData]);

  // ==================== TEMPLATE MUTATIONS ====================
  const createTemplateMutation = useMutation({
    mutationFn: (data: any) => signatureTemplatesService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signature-templates'] });
      setIsTemplateDialogOpen(false);
      resetTemplateForm();
      toast.success(t('common:x3.templateCreated', 'Vorlage erstellt'));
    },
    onError: () => toast.error(t('common:x3.templateCreateFailed', 'Fehler beim Erstellen der Vorlage')),
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => signatureTemplatesService.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signature-templates'] });
      setIsTemplateDialogOpen(false);
      resetTemplateForm();
      toast.success(t('common:x3.templateUpdated', 'Vorlage aktualisiert'));
    },
    onError: () => toast.error(t('common:x3.templateUpdateFailed', 'Fehler beim Aktualisieren der Vorlage')),
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => signatureTemplatesService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signature-templates'] });
      toast.success(t('common:x3.templateDeleted', 'Vorlage gelöscht'));
    },
    onError: () => toast.error(t('common:x3.templateDeleteFailed', 'Fehler beim Löschen der Vorlage')),
  });

  const assignMutation = useMutation({
    mutationFn: ({ id, userIds, organizationId }: { id: string; userIds: string[]; organizationId?: string }) =>
      signatureTemplatesService.assignToUsers(id, userIds, organizationId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['signature-templates'] });
      queryClient.invalidateQueries({ queryKey: ['template-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['signatures'] });
      setAssigningTemplate(null);
      toast.success(data.message);
    },
    onError: () => toast.error(t('common:x3.templateAssignFailed', 'Fehler beim Zuweisen der Vorlage')),
  });

  // Signature toggle (admin can toggle any user's signature)
  const toggleMutation = useMutation({
    mutationFn: ({ organizationId, isActive, userId }: { organizationId: string; isActive: boolean; userId?: string }) =>
      signaturesService.toggleSignatureStatus(organizationId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signatures'] });
      toast.success(t('common:state.statusUpdated', 'Status aktualisiert'));
    },
    onError: () => toast.error(t('settings:p2.msgStatusUpdateFailed', 'Fehler beim Aktualisieren des Status')),
  });

  // ==================== HELPERS ====================
  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateHtml('');
    setTemplateText('');
    setTemplateOrgId('');
    setTemplateIsDefault(false);
    setTemplatePreviewMode('html');
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim() || !templateHtml.trim()) return;
    const data = {
      name: templateName.trim(),
      signatureHtml: templateHtml.trim(),
      signatureText: templateText.trim() || undefined,
      organizationId: (templateOrgId && templateOrgId !== 'all') ? templateOrgId : undefined,
      isDefault: templateIsDefault,
    };
    if (editingTemplate) {
      await updateTemplateMutation.mutateAsync({ id: editingTemplate.id, data });
    } else {
      await createTemplateMutation.mutateAsync(data);
    }
  };

  const handleEditTemplate = (template: SignatureTemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateHtml(template.signature_html);
    setTemplateText(template.signature_text || '');
    setTemplateOrgId(template.organization_id || 'all');
    setTemplateIsDefault(template.is_default);
    setIsTemplateDialogOpen(true);
  };

  const handleDeleteTemplate = async (id: string) => {
    if (confirm(t('common:x3.sureAssignmentsRemoved', 'Sind Sie sicher? Zuweisungen werden ebenfalls entfernt.'))) {
      await deleteTemplateMutation.mutateAsync(id);
    }
  };

  const handleAssignTemplate = (template: SignatureTemplate) => {
    setAssigningTemplate(template);
    setSelectedUserIds([]);
    setAssignmentSearch('');
  };

  const handleSaveAssignment = async () => {
    if (!assigningTemplate) return;
    await assignMutation.mutateAsync({
      id: assigningTemplate.id,
      userIds: selectedUserIds,
      organizationId: assigningTemplate.organization_id || undefined,
    });
  };

  const getOrgName = (orgId: string | null) => {
    if (!orgId) return 'Alle Organisationen';
    const org = organizations.find(o => o.id === orgId);
    return org?.name || 'Unbekannt';
  };

  const filteredAgents = agents.filter(agent => {
    if (!assignmentSearch) return true;
    const search = assignmentSearch.toLowerCase();
    return agent.name?.toLowerCase().includes(search) || agent.email?.toLowerCase().includes(search);
  });

  const filteredSignatures = signatures.filter(sig => {
    if (!sigSearch) return true;
    const s = sigSearch.toLowerCase();
    return (
      sig.organization_name?.toLowerCase().includes(s) ||
      sig.user_first_name?.toLowerCase().includes(s) ||
      sig.user_last_name?.toLowerCase().includes(s) ||
      sig.user_email?.toLowerCase().includes(s) ||
      sig.template_name?.toLowerCase().includes(s)
    );
  });

  if (error) {
    return (
      <div className="flex items-center justify-center py-8 text-red-500">
        <AlertTriangle className="h-6 w-6 mr-2" />
        <div>
          <div className="text-sm font-medium">{t('common:state.errorOccurred', 'Fehler aufgetreten!')}</div>
          <div className="text-xs">{(error as any)?.message || 'Unbekannter Fehler'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ==================== ADMIN: TEMPLATE MANAGEMENT ==================== */}
      {isAdmin && (
        <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Crown className="h-4 w-4 text-amber-500" />
                <CardTitle className="text-base font-semibold text-slate-900">{t('common:x3.signatureTemplates', 'Signatur-Vorlagen')}</CardTitle>
                <Badge variant="secondary" className="text-[10px] h-5 px-2 bg-amber-50 text-amber-700 border-amber-200">Admin</Badge>
                <span className="text-xs text-slate-400 font-normal">({templates.length})</span>
              </div>
              <CardDescription className="text-sm text-slate-500">
                {t('common:x3.centralSignatureDesc', 'Zentrale Signatur-Vorlagen erstellen und Benutzern zuweisen.')}
              </CardDescription>
            </div>

            {/* Create Template Dialog */}
            <Dialog open={isTemplateDialogOpen} onOpenChange={(open) => { if (!open) resetTemplateForm(); setIsTemplateDialogOpen(open); }}>
              <DialogTrigger asChild>
                <Button onClick={() => resetTemplateForm()} className="h-9 gap-2 bg-slate-900 text-white hover:bg-slate-800">
                  <Plus className="h-4 w-4" />
                  {t('common:x3.newTemplate', 'Neue Vorlage')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
                <div className="flex flex-1 overflow-hidden">
                  {/* Sidebar */}
                  <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
                    <div>
                      <span className="text-sm font-semibold text-slate-900">
                        {editingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage'}
                      </span>
                      <p className="text-xs text-slate-500 leading-relaxed mt-2">
                        Erstellen Sie eine zentrale Signatur-Vorlage, die mehreren Benutzern zugewiesen werden kann.
                      </p>
                    </div>
                    <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100">
                      <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">{t('common:x1.tips', 'Tipps')}</span>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2 text-xs text-slate-600">
                          <Check className="h-3 w-3 text-emerald-500 shrink-0" /><span>{t('common:x3.hostImagesExternally', 'Bilder extern hosten')}</span>
                        </li>
                        <li className="flex items-center gap-2 text-xs text-slate-600">
                          <Check className="h-3 w-3 text-emerald-500 shrink-0" /><span>{t('common:x3.useInlineStyles', 'Inline-Styles nutzen')}</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-1 bg-white min-w-0">
                    <div className="px-6 py-4 border-b border-slate-100 flex-shrink-0">
                      <DialogTitle className="text-base font-semibold text-slate-900">{t('common:x3.templateDetails', 'Vorlagendetails')}</DialogTitle>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs font-medium text-slate-700">{t('common:x1.nameReq', 'Name *')}</Label>
                            <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder={t('common:x3.egDefaultSignature', 'z.B. Standard-Signatur')} className="h-9 border-slate-200 focus-visible:ring-slate-200" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs font-medium text-slate-700">{t('common:field.organization', 'Organisation')}</Label>
                            <Select value={templateOrgId} onValueChange={setTemplateOrgId}>
                              <SelectTrigger className="h-9 border-slate-200 focus:ring-slate-200"><SelectValue placeholder={t('common:state.allOrgs', 'Alle Organisationen')} /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">{t('common:state.allOrgs', 'Alle Organisationen')}</SelectItem>
                                {organizations.map(org => (<SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-medium text-slate-700">{t('common:x3.htmlSignatureReq', 'HTML-Signatur *')}</Label>
                            <div className="flex gap-1">
                              <Button variant={templatePreviewMode === 'html' ? 'secondary' : 'ghost'} size="sm" onClick={() => setTemplatePreviewMode('html')} className="h-6 px-2 text-[10px]">HTML</Button>
                              <Button variant={templatePreviewMode === 'text' ? 'secondary' : 'ghost'} size="sm" onClick={() => setTemplatePreviewMode('text')} className="h-6 px-2 text-[10px]">{t('common:x3.text', 'Text')}</Button>
                            </div>
                          </div>
                          {templatePreviewMode === 'html' ? (
                            <div className="space-y-4">
                              <Textarea value={templateHtml} onChange={(e) => setTemplateHtml(e.target.value)} placeholder='<div>Mit freundlichen Grüßen,<br><strong>Ihr Name</strong></div>' className="font-mono text-xs min-h-[200px] rounded-lg border-slate-200 focus-visible:ring-slate-200" />
                              {templateHtml && (
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                  <p className="text-[10px] uppercase font-bold text-slate-400 mb-2">{t('common:field.preview', 'Vorschau')}</p>
                                  <div className="text-sm bg-white p-4 rounded border border-slate-100" dangerouslySetInnerHTML={{ __html: templateHtml }} />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <Textarea value={templateText} onChange={(e) => setTemplateText(e.target.value)} placeholder="Mit freundlichen Grüßen,&#10;Ihr Name" className="font-mono text-xs min-h-[200px] rounded-lg border-slate-200 focus-visible:ring-slate-200" />
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-3">
                          <Checkbox id="template-default" checked={templateIsDefault} onCheckedChange={(checked) => setTemplateIsDefault(checked === true)} />
                          <Label htmlFor="template-default" className="text-xs text-slate-600 cursor-pointer">{t('common:x3.setAsDefaultTemplate', 'Als Standard-Vorlage festlegen')}</Label>
                        </div>
                      </div>
                    </div>

                    <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
                      <Button variant="outline" onClick={() => { setIsTemplateDialogOpen(false); resetTemplateForm(); }} className="h-9 rounded-lg border-slate-200 text-slate-600">{t('common:action.cancel', 'Abbrechen')}</Button>
                      <Button onClick={handleSaveTemplate} disabled={!templateName.trim() || !templateHtml.trim() || createTemplateMutation.isPending || updateTemplateMutation.isPending} className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                        {(createTemplateMutation.isPending || updateTemplateMutation.isPending) ? (<><Loader2 className="h-3 w-3 mr-2 animate-spin" />{t('common:state.savingShort', 'Speichert...')}</>) : (<><Save className="h-3 w-3 mr-2" />{editingTemplate ? 'Aktualisieren' : 'Erstellen'}</>)}
                      </Button>
                    </DialogFooter>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>

          <CardContent>
            {isLoadingTemplates ? (
              <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />{t('common:state.loading', 'Laden...')}</div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                <FileSignature className="h-8 w-8 mb-2 opacity-20" />
                <p className="text-sm">{t('common:x3.noTemplatesYet', 'Noch keine Vorlagen erstellt')}</p>
              </div>
            ) : (
              <div className="rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="text-left font-semibold text-slate-900 h-11 px-4">{t('common:field.name', 'Name')}</th>
                      <th className="text-left font-semibold text-slate-900 h-11 px-4">{t('common:field.organization', 'Organisation')}</th>
                      <th className="text-left font-semibold text-slate-900 h-11 px-4">{t('views:f.sAssigned', 'Zugewiesen')}</th>
                      <th className="text-left font-semibold text-slate-900 h-11 px-4">{t('common:field.status', 'Status')}</th>
                      <th className="text-right font-semibold text-slate-900 h-11 px-4">{t('common:field.actions', 'Aktionen')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map((template) => (
                      <tr key={template.id} className="border-b border-slate-100 hover:bg-slate-50/50 group">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-700">{template.name}</span>
                            {template.is_default && <Badge variant="secondary" className="text-[10px] h-4 px-1.5 bg-amber-50 text-amber-600 border-amber-200">{t('common:x1.standard', 'Standard')}</Badge>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-500">{getOrgName(template.organization_id)}</td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className="text-[10px] h-5 px-2 bg-blue-50 text-blue-700 border-blue-200">
                            <Users className="h-3 w-3 mr-1" />{template.assigned_count || 0} Benutzer
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className={cn("h-5 px-2 text-[10px] font-medium border", template.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-100 text-slate-600 border-slate-200")}>
                            {template.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" onClick={() => handleAssignTemplate(template)} className="h-8 w-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50" title={t('ticketList:action.assign', 'Zuweisen')}><UserPlus className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleEditTemplate(template)} className="h-8 w-8 text-slate-500 hover:text-slate-900 hover:bg-slate-100" title={t('common:action.edit', 'Bearbeiten')}><Edit className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)} className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50" title={t('common:action.delete', 'Löschen')}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ==================== ASSIGNMENT DIALOG ==================== */}
      {assigningTemplate && (
        <Dialog open={!!assigningTemplate} onOpenChange={(open) => !open && setAssigningTemplate(null)}>
          <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden border-0 rounded-2xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <DialogTitle className="text-base font-semibold text-slate-900">{t('common:x3.assignTemplate', 'Vorlage zuweisen')}</DialogTitle>
              <p className="text-xs text-slate-500 mt-1">"{assigningTemplate.name}" — Wählen Sie Benutzer aus.</p>
            </div>
            <div className="px-6 py-3 border-b border-slate-100 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input placeholder={t('common:x3.searchUsers', 'Benutzer suchen...')} value={assignmentSearch} onChange={(e) => setAssignmentSearch(e.target.value)} className="pl-9 h-9 text-sm border-slate-200 focus-visible:ring-slate-200" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-2">
              <button type="button" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors mb-1" onClick={() => { selectedUserIds.length === filteredAgents.length ? setSelectedUserIds([]) : setSelectedUserIds(filteredAgents.map(a => a.id)); }}>
                <Checkbox checked={filteredAgents.length > 0 && selectedUserIds.length === filteredAgents.length} className="pointer-events-none" />
                <span className="text-xs font-semibold text-slate-500">{t('common:x3.selectAllCount', 'Alle auswählen (')}{filteredAgents.length})</span>
              </button>
              <div className="space-y-0.5">
                {filteredAgents.map(agent => (
                  <button key={agent.id} type="button" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors" onClick={() => setSelectedUserIds(prev => prev.includes(agent.id) ? prev.filter(id => id !== agent.id) : [...prev, agent.id])}>
                    <Checkbox checked={selectedUserIds.includes(agent.id)} className="pointer-events-none" />
                    <div className="flex-1 text-left">
                      <span className="text-sm text-slate-700">{agent.name}</span>
                      <span className="text-xs text-slate-400 ml-2">{agent.email}</span>
                    </div>
                  </button>
                ))}
              </div>
              {filteredAgents.length === 0 && <div className="text-center py-6 text-slate-400 text-sm">{t('common:state.noUsers', 'Keine Benutzer gefunden')}</div>}
            </div>
            <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center flex-shrink-0">
              <span className="text-xs text-slate-500">{selectedUserIds.length} ausgewählt</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAssigningTemplate(null)} className="h-9 rounded-lg border-slate-200 text-slate-600">{t('common:action.cancel', 'Abbrechen')}</Button>
                <Button onClick={handleSaveAssignment} disabled={assignMutation.isPending} className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                  {assignMutation.isPending ? (<><Loader2 className="h-3 w-3 mr-2 animate-spin" />{t('common:state.savingShort', 'Speichert...')}</>) : (<><UserPlus className="h-3 w-3 mr-2" />{t('ticketList:action.assign', 'Zuweisen')}</>)}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ==================== ALL SIGNATURES (Admin: all users, Agent: own) ==================== */}
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base font-semibold text-slate-900">
                {isAdmin ? 'Alle Benutzer-Signaturen' : 'Meine Signaturen'}
              </CardTitle>
              <span className="text-xs text-slate-400 font-normal">({signatures.length})</span>
            </div>
            <CardDescription className="text-sm text-slate-500">
              {isAdmin
                ? 'Übersicht aller zugewiesenen und persönlichen Signaturen.'
                : 'Ihre aktiven E-Mail-Signaturen.'}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {isAdmin && signatures.length > 3 && (
            <div className="mb-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input placeholder={t('common:x1.searchSignatures', 'Signaturen suchen...')} value={sigSearch} onChange={(e) => setSigSearch(e.target.value)} className="pl-9 h-9 text-sm border-slate-200 focus-visible:ring-slate-200" />
              </div>
            </div>
          )}

          {isLoadingSignatures ? (
            <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />{t('common:state.loading', 'Laden...')}</div>
          ) : filteredSignatures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <FileSignature className="h-8 w-8 mb-2 opacity-20" />
              <p className="text-sm">{isAdmin ? 'Noch keine Signaturen vorhanden. Erstellen Sie eine Vorlage und weisen Sie diese Benutzern zu.' : 'Keine Signaturen. Ihr Admin wird Ihnen eine Signatur zuweisen.'}</p>
            </div>
          ) : (
            <div className="rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    {isAdmin && <th className="text-left font-semibold text-slate-900 h-11 px-4">{t('common:field.users', 'Benutzer')}</th>}
                    <th className="text-left font-semibold text-slate-900 h-11 px-4">{t('common:field.organization', 'Organisation')}</th>
                    <th className="text-left font-semibold text-slate-900 h-11 px-4">{t('common:x3.source', 'Quelle')}</th>
                    <th className="text-left font-semibold text-slate-900 h-11 px-4">{t('common:field.preview', 'Vorschau')}</th>
                    <th className="text-left font-semibold text-slate-900 h-11 px-4">{t('common:field.status', 'Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSignatures.map((sig) => (
                    <tr key={sig.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      {isAdmin && (
                        <td className="px-4 py-3">
                          <div>
                            <span className="font-medium text-slate-700">{sig.user_first_name} {sig.user_last_name}</span>
                            <span className="text-xs text-slate-400 block">{sig.user_email}</span>
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3 text-slate-600">{sig.organization_name || getOrgName(sig.organization_id)}</td>
                      <td className="px-4 py-3">
                        {sig.template_name ? (
                          <Badge variant="secondary" className="text-[10px] h-5 px-2 bg-blue-50 text-blue-700 border-blue-200">
                            {t('common:x3.templateColon', 'Vorlage:')} {sig.template_name}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] h-5 px-2 bg-slate-100 text-slate-600 border-slate-200">
                            {t('common:x3.personal', 'Persönlich')}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-[300px] truncate text-slate-500 text-xs">
                          {sig.signature_text || <span className="italic opacity-50">{t('common:x3.htmlOnlyShort', 'Nur HTML')}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={cn("h-5 px-2 text-[10px] font-medium border", sig.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-100 text-slate-600 border-slate-200")}>
                          {sig.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
